// "Tematy" tab: themes, strengths / weaknesses, topics and products.
import { COLORS } from "./constants";
import {
  BarList,
  Card,
  EmptyState,
  RatingCell,
  SentimentBar,
  SortableTable,
} from "./ui";
import { nf, pct, reviewsWord } from "./utils";

const TopicsTab = ({ data, onDrill }) => {
  const { themes, strengths, weaknesses, topics, products } = data;
  const praise = [...themes]
    .filter((t) => t.praise_mentions)
    .sort((a, b) => b.praise_mentions - a.praise_mentions);
  const complaints = [...themes]
    .filter((t) => t.complaint_mentions)
    .sort((a, b) => b.complaint_mentions - a.complaint_mentions);
  const improvement = themes
    .filter((t) => t.complaint_mentions > 0)
    .map((t) => ({
      ...t,
      complaint_share: pct(
        t.complaint_mentions,
        t.praise_mentions + t.complaint_mentions,
      ),
    }));

  return (
    <div className="gr-tab">
      <div className="gr-grid gr-grid-2">
        <Card
          id="gr-praise"
          title="Za co klienci chwalą"
          subtitle="Wzmianki o mocnych stronach pogrupowane w kategorie"
          exportName="pochwaly-kategorie"
        >
          <BarList
            items={praise.map((t) => ({
              key: t.key,
              label: t.label,
              value: t.praise_mentions,
              secondary: `w ${nf(t.praise_reviews)} ${reviewsWord(t.praise_reviews)} · ${t.top_praise.slice(0, 3).join(", ")}`,
            }))}
            color={COLORS.positive}
            onSelect={(item) =>
              onDrill({ theme: item.key, themeSide: "praise" })
            }
          />
        </Card>
        <Card
          id="gr-complaints"
          title="Na co klienci narzekają"
          subtitle="Wzmianki o słabych stronach pogrupowane w kategorie"
          exportName="skargi-kategorie"
        >
          <BarList
            items={complaints.map((t) => ({
              key: t.key,
              label: t.label,
              value: t.complaint_mentions,
              secondary: `w ${nf(t.complaint_reviews)} ${reviewsWord(t.complaint_reviews)} · ${t.top_complaints.slice(0, 3).join(", ")}`,
            }))}
            color={COLORS.negative}
            onSelect={(item) =>
              onDrill({ theme: item.key, themeSide: "complaint" })
            }
            emptyText="Brak uwag krytycznych w wybranym zakresie"
          />
        </Card>
      </div>

      <Card
        title="Obszary do poprawy"
        subtitle="Kategorie ze skargami – udział skarg w wzmiankach pokazuje, gdzie opinie są najbardziej podzielone"
      >
        <SortableTable
          rowKey={(r) => r.key}
          rows={improvement}
          initialSort={{ key: "complaint_reviews", dir: "desc" }}
          onRowClick={(r) => onDrill({ theme: r.key, themeSide: "complaint" })}
          emptyText="Brak skarg w wybranym zakresie"
          columns={[
            { key: "label", label: "Kategoria", primary: true },
            {
              key: "complaint_reviews",
              label: "Opinie ze skargą",
              align: "right",
              render: (r) => nf(r.complaint_reviews),
            },
            {
              key: "praise_reviews",
              label: "Opinie z pochwałą",
              align: "right",
              render: (r) => nf(r.praise_reviews),
            },
            {
              key: "complaint_share",
              label: "Udział skarg",
              align: "right",
              render: (r) => (
                <span className="gr-share-cell">
                  <span className="gr-share-track">
                    <span style={{ width: `${r.complaint_share}%` }} />
                  </span>
                  {nf(r.complaint_share, 0)}%
                </span>
              ),
            },
            {
              key: "top_complaints",
              label: "Przykładowe skargi",
              sortable: false,
              render: (r) => (
                <span className="gr-muted">
                  {r.top_complaints.slice(0, 2).join(", ")}
                </span>
              ),
            },
          ]}
        />
      </Card>

      <div className="gr-grid gr-grid-2">
        <Card
          title="Najczęstsze mocne strony"
          subtitle="Dokładne sformułowania z analizy opinii"
        >
          <BarList
            items={strengths.map((s) => ({
              key: s.item,
              label: s.item,
              value: s.count,
            }))}
            color={COLORS.positive}
            limit={8}
            onSelect={(item) => onDrill({ strength: item.key })}
          />
        </Card>
        <Card
          title="Najczęstsze słabe strony"
          subtitle="Dokładne sformułowania z analizy opinii"
        >
          <BarList
            items={weaknesses.map((s) => ({
              key: s.item,
              label: s.item,
              value: s.count,
            }))}
            color={COLORS.negative}
            limit={8}
            onSelect={(item) => onDrill({ weakness: item.key })}
            emptyText="Brak słabych stron w wybranym zakresie"
          />
        </Card>
      </div>

      <div className="gr-grid gr-grid-8-4">
        <Card
          title="Kluczowe tematy opinii"
          subtitle="Temat przypisany przez AI (key_topic) – kliknij wiersz, aby zobaczyć opinie"
        >
          <SortableTable
            rowKey={(r) => r.key}
            rows={topics}
            initialSort={{ key: "count", dir: "desc" }}
            onRowClick={(r) => onDrill({ topic: r.key })}
            columns={[
              { key: "label", label: "Temat", primary: true },
              {
                key: "count",
                label: "Opinie",
                align: "right",
                render: (r) => nf(r.count),
              },
              {
                key: "average_rating",
                label: "Śr. ocena",
                align: "right",
                render: (r) => <RatingCell value={r.average_rating} />,
              },
              {
                key: "average_sentiment",
                label: "Sentyment",
                align: "right",
                render: (r) => `${nf(r.average_sentiment, 1)}/10`,
              },
              {
                key: "mix",
                label: "Rozkład",
                sortable: false,
                render: (r) => (
                  <SentimentBar
                    positive={r.positive_count}
                    neutral={r.count - r.positive_count - r.negative_count}
                    negative={r.negative_count}
                  />
                ),
              },
            ]}
          />
        </Card>
        <Card
          title="Produkty w opiniach"
          subtitle="Wykryte na podstawie tematu i treści opinii"
        >
          {products.length ? (
            <ul className="gr-products">
              {products.map((p) => (
                <li key={p.key}>
                  <button
                    type="button"
                    onClick={() => onDrill({ product: p.key })}
                  >
                    <span className="gr-products-name">{p.label}</span>
                    <span className="gr-products-stats">
                      <strong>{nf(p.count)}</strong> {reviewsWord(p.count)} ·{" "}
                      <RatingCell value={p.average_rating} />
                    </span>
                    <SentimentBar
                      positive={p.positive_count}
                      neutral={p.count - p.positive_count - p.negative_count}
                      negative={p.negative_count}
                    />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Brak wzmianek o produktach" />
          )}
        </Card>
      </div>
    </div>
  );
};

export default TopicsTab;
